import React from 'react';
import { motion } from 'framer-motion';
import { Scale, Shield, MapPin, Zap, FileCheck, Euro } from 'lucide-react';

export const BenefitsSection = () => {
  const benefits = [
    {
      icon: Scale,
      title: 'Unabhängig & Neutral',
      description: 'Wir arbeiten ausschließlich für Sie – nicht für die Versicherung. Ihre Interessen stehen an erster Stelle.',
      color: 'bg-[#22c55e]',
      large: true
    },
    {
      icon: Zap,
      title: 'Schnell vor Ort',
      description: 'Termin in 30 Minuten. Wir kommen zu Ihnen.',
      color: 'bg-blue-500',
      large: false
    },
    {
      icon: Euro,
      title: 'Kostenlos für Sie',
      description: 'Bei unverschuldetem Unfall zahlt die Gegenseite.',
      color: 'bg-amber-500',
      large: false
    },
    {
      icon: FileCheck,
      title: 'Rechtssicher dokumentiert',
      description: 'Gerichtsfestes Gutachten mit allen Ansprüchen: Wertminderung, Nutzungsausfall, Reparaturkosten.',
      color: 'bg-violet-500',
      large: true
    },
    {
      icon: MapPin,
      title: 'Bundesweit',
      description: 'Hamburg, Rhein-Main und deutschlandweit.',
      color: 'bg-rose-500',
      large: false
    },
    {
      icon: Shield,
      title: 'Erfahrene Gutachter',
      description: 'Qualifizierte Sachverständige mit jahrelanger Erfahrung.',
      color: 'bg-cyan-500',
      large: false
    },
  ];

  return (
    <section className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div 
          className="max-w-3xl mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block px-4 py-1.5 bg-[#22c55e]/10 text-[#22c55e] rounded-full text-sm font-semibold mb-4 uppercase tracking-wider">
            Warum wir?
          </span>
          <h2 
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#0f172a] mb-6 tracking-tight leading-tight"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Soforthilfe
            <br />
            <span className="bg-gradient-to-r from-[#22c55e] to-[#4ade80] bg-clip-text text-transparent">
              nach dem Unfall
            </span>
          </h2>
          <p className="text-xl text-slate-600 leading-relaxed">
            Wir verstehen: Nach einem Unfall ist alles chaotisch. Deshalb machen wir es Ihnen so einfach wie möglich.
          </p>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((benefit, idx) => (
            <motion.div
              key={idx}
              className={`group relative p-8 rounded-3xl bg-slate-50 border border-slate-100 hover:border-slate-200 hover:shadow-[0_20px_40px_-12px_rgba(0,0,0,0.1)] transition-all duration-500 overflow-hidden cursor-default
                ${benefit.large ? 'lg:col-span-2' : 'lg:col-span-1'}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1, duration: 0.5 }}
              whileHover={{ y: -5 }}
            >
              {/* Background Gradient on Hover */}
              <div className={`absolute inset-0 ${benefit.color} opacity-0 group-hover:opacity-[0.03] transition-opacity duration-500`} />
              
              {/* Icon */}
              <div className={`w-14 h-14 ${benefit.color} rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                <benefit.icon className="w-7 h-7 text-white" />
              </div>
              
              {/* Content */}
              <h3 
                className="text-xl font-bold text-[#0f172a] mb-3 group-hover:text-[#22c55e] transition-colors"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {benefit.title}
              </h3>
              <p className="text-slate-600 leading-relaxed">
                {benefit.description}
              </p>

              {/* Decorative Corner */}
              <div className={`absolute -bottom-10 -right-10 w-32 h-32 ${benefit.color} rounded-full opacity-5 group-hover:opacity-10 transition-opacity`} />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};